import { useCallback, useEffect, useRef, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { subscribeToClassRidesPulled } from "../local/rideCacheEvents";

// Card #2064 — "Excused" (DR123.3's fourth term reason), the presiding judge
// (judge position C) or an admin only. Modeled directly on useRideNoShow
// (card #1992): Excused is a RIDE-level fact (score-api's ride.excused, not
// one judge's own ScoreSheet), so every judge position scoring the SAME ride
// sees the SAME mark, delivered by the 5s change check (#3200, one fetch on open, then the class re-pull) rather than a per-ride poll. That's how a ruling raised at C waterfalls to every
// other panel judge's sheet.
//
// mark()/undo() are DIRECT writes, not queued through the offline sync
// engine — same choice #1992's No Show and #916's Scratch/No Show gavel
// items made (see ScoringForm.jsx's confirmTermReason): this is a live
// cloud write with its own confirm step and loading/error state, not
// something the autosave queue should replay later out of order.

/**
 * @param {string} rideId
 * @returns {{
 *   excused: object|null,
 *   loading: boolean,
 *   error: Error|null,
 *   mark: () => Promise<void>,
 *   undo: () => Promise<void>,
 *   marking: boolean,
 *   markError: Error|null,
 * }}
 */
// card #3196 - `online` (default true) skips the fetch while offline: these are
// live, shared ride facts with nothing local to read, so offline keeps the last
// value and makes no request; the online flip re-runs the effect and refreshes.
export function useRideExcused(rideId, { online = true } = {}) {
  const onlineRef = useRef(online);
  onlineRef.current = online;
  const scoreApi = useScoreApi();
  const [excused, setExcused] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(null);

  const rideIdRef = useRef(rideId);
  rideIdRef.current = rideId;
  const apiRef = useRef(scoreApi);
  apiRef.current = scoreApi;

  // Same stale-response guard as useRideNoShow: a fetch that started
  // before a mark()/undo() lands must not clobber the fresher state with
  // its own late-arriving read.
  const refreshGenRef = useRef(0);

  const refresh = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    if (!onlineRef.current) {
      setLoading(false);
      return;
    }
    const gen = ++refreshGenRef.current;
    const isStale = () => gen !== refreshGenRef.current;
    try {
      const result = unwrap(await apiRef.current.rides.getRideExcused(id)) ?? null;
      if (isStale()) return;
      setExcused(result);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError(err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, []);

  // card #3200 - one fetch when the sheet opens (and when connectivity
  // returns), NO interval. After that the value arrives with the class re-pull
  // the 5s change check does when any ride in this class changes: the pulled
  // ride row carries `excused`, so no per-ride request is needed (see
  // local/rideCacheEvents.js).
  useEffect(() => {
    if (!rideId) {
      setExcused(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    refresh();
    return undefined;
  }, [rideId, refresh, online]);

  useEffect(
    () =>
      subscribeToClassRidesPulled(({ rides: pulled }) => {
        const id = rideIdRef.current;
        if (!id) return;
        const row = pulled.find((r) => r.id === id);
        if (!row) return;
        // supersedes any fetch still in flight
        refreshGenRef.current += 1;
        setExcused(row.excused ?? null);
        setError(null);
        setLoading(false);
      }),
    [],
  );

  const mark = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      const result = unwrap(await apiRef.current.rides.markRideExcused(id));
      setExcused(result);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  const undo = useCallback(async () => {
    const id = rideIdRef.current;
    if (!id) return;
    setMarking(true);
    setMarkError(null);
    try {
      await apiRef.current.rides.undoRideExcused(id);
      setExcused(null);
    } catch (err) {
      setMarkError(err);
      throw err;
    } finally {
      setMarking(false);
    }
  }, []);

  return { excused, loading, error, mark, undo, marking, markError };
}

export default useRideExcused;
